import React, { useMemo } from 'react';
import Lottie from 'lottie-react';
import { BaseModal, FilledButton, SvgIcon } from '@make-software/tg-mini-app-ui';

import {
  CircleIconWrapper,
  ModalContentWrapper,
  StyledCloseIcon,
  StyledTransactionProcessHeader
} from '@/shared';
import { TxStatus } from '@/entities';
import CloseIcon from '@/shared/assets/icons/ic-close.svg';
import TxDoneIcon from '@/shared/assets/icons/ic-illustration-done.svg';
import TxSentError from '@/shared/assets/icons/ic-illustration-wrong.svg';
import loadingAnimation from '@/shared/assets/lottie/loading-animation.json';

import { AnimationContainer, Content, Description, Title, processModalStyle } from '../styled';

const TITLES: Record<TxStatus, string> = {
  processing: 'Please wait',
  processed: 'Done!',
  error: 'Something went wrong'
};

const DESCRIPTIONS: Record<TxStatus, string> = {
  processing: 'Setting the subname as your primary CSPR name',
  processed: 'Enjoy your new CSPR name!',
  error: "We couldn't complete your transaction. Please try again or check your wallet connection."
};

interface IMakeSubnamePrimaryModalProps {
  status: TxStatus;
  isOpen: boolean;
  onClose: () => void;
}

export const MakeSubnamePrimaryModal: React.FC<IMakeSubnamePrimaryModalProps> = ({
                                                                                   status,
                                                                                   isOpen,
                                                                                   onClose
                                                                                 }) => {
  const isProcessing = status === 'processing';
  const isSuccess = status === 'processed';

  const title = useMemo(() => TITLES[status], [status]);
  const description = useMemo(() => DESCRIPTIONS[status], [status]);
  const primaryLabel = isProcessing ? null : isSuccess ? 'Close' : 'Try again';

  return (
    <BaseModal
      isOpen={isOpen}
      onRequestClose={onClose}
      shouldCloseOnEsc
      shouldCloseOnOverlayClick
      variant="centered"
      centeredWidth="400px"
      centeredHeight="420px"
      style={processModalStyle}
    >
      <ModalContentWrapper direction="column">
        <StyledTransactionProcessHeader align="center" justify="flex-end">
          <CircleIconWrapper onClick={onClose} role="button" aria-label="Close modal">
            <StyledCloseIcon src={CloseIcon} />
          </CircleIconWrapper>
        </StyledTransactionProcessHeader>

        <Content align="center" isTransactionProcessing={isProcessing}>
          {isProcessing ? (
            <AnimationContainer align="center" justify="center">
              <Lottie animationData={loadingAnimation} style={{ height: 220, width: 220 }} loop />
            </AnimationContainer>
          ) : (
            <SvgIcon src={isSuccess ? TxDoneIcon : TxSentError} size={180} />
          )}

          <Title weight={3} color="#FFFFFF">
            {title}
          </Title>
          <Description weight={3} isTransactionProcessing={isProcessing}>
            {description}
          </Description>

          {primaryLabel && <FilledButton title={primaryLabel} onClick={onClose} />}
        </Content>
      </ModalContentWrapper>
    </BaseModal>
  );
};
